// Search in the docs, without a backend: the index is built once from the pages' JSX, one entry per h2 (and
// one for what comes before the first h2, the lead included), and a query is a set of words that must all
// appear in an entry. A word in the heading or the page title weighs more than one in the body.

export type DocEntry = {
  slug: string;
  /** The page's title */
  title: string;
  /** The h2 the text sits under; none for the text before the first heading */
  heading?: { id: string; text: string };
  /** The entry's text, blocks separated by " · " */
  text: string;
};

export type DocHit = {
  entry: DocEntry;
  score: number;
  /** The passage around the first match, split so the matches can be highlighted */
  snippet: { text: string; hit: boolean }[];
};

type Element = { type?: unknown; props?: { id?: unknown; children?: unknown } };

/** Elements whose text does not run into their neighbours' */
const BLOCKS = new Set(['p', 'li', 'ul', 'ol', 'h1', 'h2', 'h3', 'h4', 'pre', 'aside', 'table', 'thead', 'tbody', 'tr', 'th', 'td', 'div', 'br', 'blockquote']);
const SEPARATOR = ' · ';
/** How far the snippet reaches before and after the first match */
const SNIPPET_BEFORE = 60;
const SNIPPET_AFTER = 110;
/** Weights: a word in the heading, in the title, each occurrence in the text (capped), the whole phrase in order */
const HEADING_WEIGHT = 4;
const TITLE_WEIGHT = 2;
const TEXT_CAP = 5;
const PHRASE_BONUS = 3;

function isElement(node: unknown): node is Element {
  return typeof node === 'object' && node !== null && 'props' in node;
}

/** Collects the text of a JSX tree the way the DOM would, with a line break around every block element */
function collect(node: unknown, out: string[]): void {
  if (node == null || typeof node === 'boolean') return;
  if (typeof node === 'string' || typeof node === 'number') {
    out.push(String(node));
    return;
  }
  if (Array.isArray(node)) {
    for (const n of node) collect(n, out);
    return;
  }
  if (!isElement(node)) return;
  const block = typeof node.type === 'string' && BLOCKS.has(node.type);
  if (block) out.push('\n');
  collect(node.props?.children, out);
  if (block) out.push('\n');
}

/** One line of text: blocks separated by a middle dot, no doubled spaces */
function tidy(raw: string): string {
  return raw
    .replace(/[ \t]+/g, ' ')
    .replace(/\s*\n\s*/g, '\n')
    .replace(/\n+/g, SEPARATOR)
    .replace(/^(\s*·\s*)+|(\s*·\s*)+$/g, '')
    .trim();
}

export function textOf(node: unknown): string {
  const out: string[] = [];
  collect(node, out);
  return tidy(out.join(''));
}

/** The search index of the given pages, in reading order */
export function indexDocs(pages: { slug: string; title: string; lead: string; body: unknown }[]): DocEntry[] {
  const index: DocEntry[] = [];
  for (const page of pages) {
    let heading: DocEntry['heading'];
    let buf: string[] = [page.lead, '\n'];
    const flush = () => {
      const text = tidy(buf.join(''));
      if (text || heading) index.push({ slug: page.slug, title: page.title, heading, text });
      buf = [];
    };
    const walk = (node: unknown): void => {
      if (node == null || typeof node === 'boolean') return;
      if (typeof node === 'string' || typeof node === 'number') {
        buf.push(String(node));
        return;
      }
      if (Array.isArray(node)) {
        for (const n of node) walk(n);
        return;
      }
      if (!isElement(node)) return;
      if (node.type === 'h2') {
        flush();
        heading = { id: typeof node.props?.id === 'string' ? node.props.id : '', text: textOf(node.props?.children) };
        return;
      }
      const block = typeof node.type === 'string' && BLOCKS.has(node.type);
      if (block) buf.push('\n');
      walk(node.props?.children);
      if (block) buf.push('\n');
    };
    walk(page.body);
    flush();
  }
  return index;
}

const escapeRegExp = (s: string) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

/** A query word matches at the start of a word: "liquidat" finds "liquidations", "min" does not find "admin" */
const WORD_START = '(?<![\\p{L}\\p{N}])';
const WORD_REST = '[\\p{L}\\p{N}]*';
const wordRegExp = (w: string) => new RegExp(WORD_START + escapeRegExp(w), 'giu');
const phraseRegExp = (words: string[]) => new RegExp(WORD_START + words.map(escapeRegExp).join('\\s+'), 'iu');
/** Marks whole words, the longest query word first so "houses" is one mark when "house" is also a query word */
const markRegExp = (words: string[]) =>
  new RegExp(`${WORD_START}(?:${[...words].sort((a, b) => b.length - a.length).map(escapeRegExp).join('|')})${WORD_REST}`, 'giu');

function occurrences(haystack: string, re: RegExp): number {
  let n = 0;
  for (const _ of haystack.matchAll(re)) n++;
  return n;
}

/** The passage of `text` around position `at`, cut at word boundaries, with the query words marked */
function snippetOf(text: string, at: number, mark: RegExp): DocHit['snippet'] {
  let start = Math.max(0, at - SNIPPET_BEFORE);
  let end = Math.min(text.length, at + SNIPPET_AFTER);
  if (start > 0) {
    const space = text.indexOf(' ', start);
    if (space !== -1 && space < at) start = space + 1;
  }
  if (end < text.length) {
    const space = text.lastIndexOf(' ', end);
    if (space > at) end = space;
  }
  const passage = (start > 0 ? '…' : '') + text.slice(start, end) + (end < text.length ? '…' : '');
  const parts: DocHit['snippet'] = [];
  let last = 0;
  for (const m of passage.matchAll(mark)) {
    if (m.index > last) parts.push({ text: passage.slice(last, m.index), hit: false });
    parts.push({ text: m[0], hit: true });
    last = m.index + m[0].length;
  }
  if (last < passage.length) parts.push({ text: passage.slice(last), hit: false });
  return parts;
}

/** The entries where every word of the query appears, best first; ties keep the reading order */
export function searchDocs(index: DocEntry[], query: string, limit = 20): DocHit[] {
  const words = [...new Set(query.toLowerCase().split(/\s+/).filter(Boolean))];
  if (words.length === 0) return [];
  const res = words.map(wordRegExp);
  const phrase = phraseRegExp(words);
  const mark = markRegExp(words);
  const hits: DocHit[] = [];
  for (const entry of index) {
    const heading = entry.heading?.text ?? '';
    let score = 0;
    let at = -1;
    for (const re of res) {
      const inHeading = heading.search(re) !== -1;
      const inTitle = entry.title.search(re) !== -1;
      const n = occurrences(entry.text, re);
      if (!inHeading && !inTitle && n === 0) {
        score = -1;
        break;
      }
      score += (inHeading ? HEADING_WEIGHT : 0) + (inTitle ? TITLE_WEIGHT : 0) + Math.min(n, TEXT_CAP);
      if (n > 0) {
        const i = entry.text.search(re);
        if (at === -1 || i < at) at = i;
      }
    }
    if (score < 0) continue;
    if (words.length > 1) {
      const i = entry.text.search(phrase);
      if (i !== -1 || heading.search(phrase) !== -1) score += PHRASE_BONUS;
      if (i !== -1) at = i;
    }
    hits.push({ entry, score, snippet: snippetOf(entry.text, Math.max(at, 0), mark) });
  }
  return hits.sort((a, b) => b.score - a.score).slice(0, limit);
}
